/**
 * SessionManager - Centralized session management with localStorage persistence and inactivity tracking
 * 
 * Features:
 * - Store session data in localStorage with expiry timestamp
 * - Track user activity (mouse, keyboard, scroll events)
 * - Auto-extend session on activity if approaching timeout
 * - Restore session from localStorage on page load
 * - Validate restored session with backend
 * - Clear session on expiry or inactivity
 */

class SessionManager {
  constructor() {
    this.sessionId = null;
    this.userData = null;
    this.sessionExpiresAt = null;
    this.lastActivity = null;
    this.activityTimeout = null;
    this.extensionTimeout = null;
    this.isTracking = false;

    // Configuration
    this.SESSION_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds
    this.INACTIVITY_THRESHOLD = 30 * 60 * 1000; // 30 minutes of inactivity
    this.EXTENSION_THRESHOLD = 5 * 60 * 1000; // Extend if < 5 minutes remaining
    this.ACTIVITY_CHECK_INTERVAL = 60 * 1000; // Check activity every minute
    this.HEARTBEAT_INTERVAL = 5 * 60 * 1000; // Send heartbeat every 5 minutes

    // Activity events to track
    this.activityEvents = [
      'mousemove',
      'keydown',
      'scroll',
      'click',
      'touchstart',
      'mousedown',
      'keyup'
    ];

    // Storage keys
    this.STORAGE_KEYS = {
      SESSION_ID: 'sessionId',
      USER_DATA: 'userData',
      SESSION_EXPIRES_AT: 'sessionExpiresAt',
      LAST_ACTIVITY: 'lastActivity',
      CACHED_PERMISSIONS: 'cachedPermissions'
    };
  }

  /**
   * Save session data to localStorage
   * @param {string} sessionId - Session identifier
   * @param {Object} userData - User data object
   * @param {string} expiresAt - ISO string of expiration time
   */
  saveSession(sessionId, userData, expiresAt) {
    try {
      this.sessionId = sessionId;
      this.userData = userData;
      this.sessionExpiresAt = expiresAt;
      this.lastActivity = new Date().toISOString();

      // Store in localStorage
      localStorage.setItem(this.STORAGE_KEYS.SESSION_ID, sessionId);
      localStorage.setItem(this.STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
      localStorage.setItem(this.STORAGE_KEYS.SESSION_EXPIRES_AT, expiresAt);
      localStorage.setItem(this.STORAGE_KEYS.LAST_ACTIVITY, this.lastActivity);

      console.log('💾 Session saved to localStorage:', {
        sessionId: sessionId.substring(0, 20) + '...',
        expiresAt,
        userEmail: userData?.email
      });

      return true;
    } catch (error) {
      console.error('❌ Error saving session to localStorage:', error);
      return false;
    }
  }

  /**
   * Get stored session data from localStorage
   * @returns {Object|null} Session data or null if not found
   */
  getStoredSession() {
    try {
      const sessionId = localStorage.getItem(this.STORAGE_KEYS.SESSION_ID);
      const userDataStr = localStorage.getItem(this.STORAGE_KEYS.USER_DATA);
      const sessionExpiresAt = localStorage.getItem(this.STORAGE_KEYS.SESSION_EXPIRES_AT);
      const lastActivity = localStorage.getItem(this.STORAGE_KEYS.LAST_ACTIVITY);

      if (!sessionId || !userDataStr || !sessionExpiresAt) {
        console.log('🔍 No stored session found');
        return null;
      }

      const userData = JSON.parse(userDataStr);

      const sessionData = {
        sessionId,
        userData,
        sessionExpiresAt,
        lastActivity
      };

      console.log('🔍 Retrieved stored session:', {
        sessionId: sessionId.substring(0, 20) + '...',
        expiresAt: sessionExpiresAt,
        userEmail: userData?.email
      });

      return sessionData;
    } catch (error) {
      console.error('❌ Error retrieving stored session:', error);
      return null;
    }
  }

  /**
   * Clear session data from localStorage and memory
   */
  clearSession() {
    try {
      // Clear localStorage
      Object.values(this.STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });

      // Clear memory
      this.sessionId = null;
      this.userData = null;
      this.sessionExpiresAt = null;
      this.lastActivity = null;

      // Stop tracking
      this.stopActivityTracking();

      console.log('🗑️ Session cleared from localStorage and memory');
      return true;
    } catch (error) {
      console.error('❌ Error clearing session:', error);
      return false;
    }
  }

  /**
   * Check if session has expired locally
   * @param {string} expiresAt - ISO string of expiration time
   * @returns {boolean} True if expired
   */
  isSessionExpired(expiresAt) {
    if (!expiresAt) return true;

    const now = new Date();
    const expiry = new Date(expiresAt);

    return now >= expiry;
  }

  /**
   * Validate stored session (client-side only - no backend calls)
   * @returns {Promise<Object>} Validation result
   */
  async validateStoredSession() {
    const storedSession = this.getStoredSession();

    if (!storedSession) {
      return {
        isValid: false,
        reason: 'No stored session found'
      };
    }

    // Check local expiry first
    if (this.isSessionExpired(storedSession.sessionExpiresAt)) {
      console.log('⏰ Session expired locally');
      this.clearSession();
      return {
        isValid: false,
        reason: 'Session expired locally'
      };
    }

    // Client-side validation only - no backend calls
    console.log('✅ Session validated locally (no backend call)');

    // Update local data
    this.sessionId = storedSession.sessionId;
    this.userData = storedSession.userData;
    this.sessionExpiresAt = storedSession.sessionExpiresAt;
    this.lastActivity = storedSession.lastActivity;

    return {
      isValid: true,
      sessionData: storedSession,
      backendData: null,
      warning: 'Using client-side validation only'
    };
  }

  /**
   * Initialize activity tracking
   */
  initActivityTracking() {
    if (this.isTracking) {
      console.log('🔄 Activity tracking already active');
      return;
    }

    console.log('🎯 Starting activity tracking');
    this.isTracking = true;

    // Add event listeners
    this.activityEvents.forEach(event => {
      document.addEventListener(event, this.handleActivity.bind(this), { passive: true });
    });

    // Start periodic checks
    this.startPeriodicChecks();
  }

  /**
   * Stop activity tracking
   */
  stopActivityTracking() {
    if (!this.isTracking) return;

    console.log('🛑 Stopping activity tracking');
    this.isTracking = false;

    // Remove event listeners
    this.activityEvents.forEach(event => {
      document.removeEventListener(event, this.handleActivity.bind(this));
    });

    // Clear timeouts
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
      this.activityTimeout = null;
    }

    if (this.extensionTimeout) {
      clearTimeout(this.extensionTimeout);
      this.extensionTimeout = null;
    }
  }

  /**
   * Handle user activity
   */
  handleActivity() {
    if (!this.isTracking) return;

    const now = new Date();
    this.lastActivity = now.toISOString();

    // Update localStorage
    localStorage.setItem(this.STORAGE_KEYS.LAST_ACTIVITY, this.lastActivity);

    // Check if we should extend session
    this.checkForSessionExtension();

    // Clear existing timeout and set new one
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
    }

    // Set timeout for inactivity check
    this.activityTimeout = setTimeout(() => {
      this.checkInactivity();
    }, this.INACTIVITY_THRESHOLD);
  }

  /**
   * Check for session extension based on activity
   */
  async checkForSessionExtension() {
    if (!this.sessionExpiresAt) return;

    const now = new Date();
    const expiry = new Date(this.sessionExpiresAt);
    const timeRemaining = expiry.getTime() - now.getTime();

    // If less than 5 minutes remaining, extend session
    if (timeRemaining < this.EXTENSION_THRESHOLD && timeRemaining > 0) {
      console.log('⏰ Session approaching expiry, extending...');
      await this.extendSession();
    }
  }

  /**
   * Check for inactivity
   */
  checkInactivity() {
    if (!this.lastActivity) return;

    const now = new Date();
    const lastActivityTime = new Date(this.lastActivity);
    const inactiveTime = now.getTime() - lastActivityTime.getTime();

    if (inactiveTime >= this.INACTIVITY_THRESHOLD) {
      console.log('⏰ User inactive for 30 minutes, expiring session');
      this.handleSessionExpiry();
    }
  }

  /**
   * Start periodic checks for session status
   */
  startPeriodicChecks() {
    // Check activity every minute
    setInterval(() => {
      if (this.isTracking) {
        this.checkInactivity();
      }
    }, this.ACTIVITY_CHECK_INTERVAL);

    // Send heartbeat every 5 minutes
    setInterval(() => {
      if (this.isTracking && this.sessionId) {
        this.sendHeartbeat();
      }
    }, this.HEARTBEAT_INTERVAL);
  }

  /**
   * Send heartbeat (client-side only - update local activity)
   */
  async sendHeartbeat() {
    if (!this.sessionId) return;

    // Update local activity timestamp
    this.lastActivity = new Date().toISOString();
    localStorage.setItem(this.STORAGE_KEYS.LAST_ACTIVITY, this.lastActivity);

    console.log('💓 Heartbeat sent (client-side only)');
  }

  /**
   * Extend session duration (client-side only)
   * @returns {Promise<boolean>} Success status
   */
  async extendSession() {
    if (!this.sessionId) return false;

    try {
      // Extend session locally
      const newExpiry = new Date(Date.now() + this.SESSION_DURATION).toISOString();
      this.sessionExpiresAt = newExpiry;
      localStorage.setItem(this.STORAGE_KEYS.SESSION_EXPIRES_AT, newExpiry);

      // Update last activity
      this.lastActivity = new Date().toISOString();
      localStorage.setItem(this.STORAGE_KEYS.LAST_ACTIVITY, this.lastActivity);

      console.log('✅ Session extended locally (client-side only)');
      return true;
    } catch (error) {
      console.error('❌ Session extension error:', error);
      return false;
    }
  }

  /**
   * Handle session expiry
   */
  handleSessionExpiry() {
    console.log('⏰ Session expired, clearing and notifying');

    // Clear session
    this.clearSession();

    // Dispatch custom event for components to listen to
    window.dispatchEvent(new CustomEvent('sessionExpired', {
      detail: { reason: 'Session expired due to inactivity' }
    }));

    // Set flag for multi-tab sync
    localStorage.setItem('sessionExpired', 'true');
  }

  /**
   * Get remaining session time in milliseconds
   * @returns {number} Milliseconds remaining
   */
  getRemainingTime() {
    if (!this.sessionExpiresAt) return 0;

    const now = new Date();
    const expiry = new Date(this.sessionExpiresAt);
    const remaining = expiry.getTime() - now.getTime();

    return Math.max(0, remaining);
  }

  /**
   * Get session info for display
   * @returns {Object} Session information
   */
  getSessionInfo() {
    return {
      sessionId: this.sessionId,
      userData: this.userData,
      expiresAt: this.sessionExpiresAt,
      lastActivity: this.lastActivity,
      remainingTime: this.getRemainingTime(),
      isTracking: this.isTracking
    };
  }

  /**
   * Create session after OTP verification (client-side only)
   * @param {string} email - User email
   * @param {Object} userData - User data
   * @returns {Promise<Object>} Session creation result
   */
  async createSession(email, userData) {
    try {
      // Generate session ID locally
      const sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      const expiresAt = new Date(Date.now() + this.SESSION_DURATION).toISOString();

      // Save session locally
      this.saveSession(sessionId, userData, expiresAt);

      console.log('✅ Session created locally (client-side only):', sessionId.substring(0, 20) + '...');

      return {
        success: true,
        sessionId: sessionId,
        expiresAt: expiresAt,
        userData: userData
      };
    } catch (error) {
      console.error('❌ Session creation error:', error);
      return {
        success: false,
        error: 'Failed to create session locally'
      };
    }
  }
  /**
   * Save permissions to localStorage for offline/cached access
   * @param {Object} permissions - User permissions object
   */
  savePermissions(permissions) {
    if (!permissions) return;
    try {
      localStorage.setItem(this.STORAGE_KEYS.CACHED_PERMISSIONS, JSON.stringify(permissions));
    } catch (error) {
      console.warn('Could not cache permissions:', error);
    }
  }

  /**
   * Retrieve cached permissions from localStorage
   * @returns {Object|null} Cached permissions or null
   */
  getCachedPermissions() {
    try {
      const cached = localStorage.getItem(this.STORAGE_KEYS.CACHED_PERMISSIONS);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      return null;
    }
  }
}

// Create singleton instance
const sessionManager = new SessionManager();

export default sessionManager;


